import { z } from 'zod'

export const MembersContentKindSchema = z.enum(['course', 'files'])
export const MembersContentStatusSchema = z.enum(['DRAFT', 'PUBLISHED'])
export const MembersCompletenessSchema = z.enum(['empty', 'incomplete', 'ready', 'published'])
export const MembersLessonKindSchema = z.enum(['text', 'pdf', 'video'])
export const MembersLessonStatusSchema = z.enum(['DRAFT', 'PUBLISHED'])
export const MembersAssetStatusSchema = z.enum(['PENDING', 'READY'])
export const MembersVideoStatusSchema = z.enum(['UPLOADING', 'PROCESSING', 'READY', 'FAILED'])

export const MembersReleaseRuleSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('immediate') }),
  z.object({ type: z.literal('days_after_purchase'), days: z.number().int().min(1) }),
  z.object({ type: z.literal('fixed_date'), date: z.string() }),
])

export const MembersAssetSchema = z.object({
  id: z.string(),
  lessonId: z.string().nullable(),
  name: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  position: z.number(),
  status: MembersAssetStatusSchema,
})

export const MembersVideoSchema = z.object({
  id: z.string(),
  lessonId: z.string(),
  fileName: z.string(),
  status: MembersVideoStatusSchema,
  durationSeconds: z.number().nullable(),
  sizeBytes: z.number(),
  renditions: z.array(z.string()),
  failureReason: z.string().nullable(),
  playbackUrl: z.string().nullable(),
  updatedAt: z.string(),
})

export const MembersLessonSchema = z.object({
  id: z.string(),
  moduleId: z.string(),
  title: z.string(),
  kind: MembersLessonKindSchema,
  body: z.string(),
  releaseRule: MembersReleaseRuleSchema,
  status: MembersLessonStatusSchema,
  position: z.number(),
  assets: z.array(MembersAssetSchema),
  video: MembersVideoSchema.nullable().optional(),
})

export const MembersModuleSchema = z.object({
  id: z.string(),
  title: z.string(),
  position: z.number(),
  lessons: z.array(MembersLessonSchema),
})

export const MembersAreaListItemSchema = z.object({
  productId: z.string(),
  name: z.string(),
  coverImage: z.string(),
  productType: z.enum(['course', 'ebook', 'service']),
  kind: MembersContentKindSchema,
  status: MembersContentStatusSchema,
  completeness: MembersCompletenessSchema,
  moduleCount: z.number(),
  lessonCount: z.number(),
  fileCount: z.number(),
  totalSizeBytes: z.number(),
  salesPageUrl: z.string().optional(),
  updatedAt: z.string(),
})

export const MembersAreaDetailSchema = z.object({
  productId: z.string(),
  name: z.string(),
  coverImage: z.string(),
  description: z.string().optional(),
  productType: z.enum(['course', 'ebook', 'service']),
  kind: MembersContentKindSchema,
  status: MembersContentStatusSchema,
  modules: z.array(MembersModuleSchema),
  assets: z.array(MembersAssetSchema),
  checkoutUrl: z.string().nullable().optional(),
  priceLabel: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const MembersUploadUrlSchema = MembersAssetSchema.extend({
  uploadUrl: z.string(),
})

export const MembersVideoUploadSchema = MembersVideoSchema.extend({
  uploadUrl: z.string(),
})

export const MembersAreaListSchema = z.array(MembersAreaListItemSchema)

export type MembersContentKind = z.infer<typeof MembersContentKindSchema>
export type MembersContentStatus = z.infer<typeof MembersContentStatusSchema>
export type MembersCompleteness = z.infer<typeof MembersCompletenessSchema>
export type MembersLessonKind = z.infer<typeof MembersLessonKindSchema>
export type MembersReleaseRule = z.infer<typeof MembersReleaseRuleSchema>
export type MembersAsset = z.infer<typeof MembersAssetSchema>
export type MembersLesson = z.infer<typeof MembersLessonSchema>
export type MembersModule = z.infer<typeof MembersModuleSchema>
export type MembersAreaListItem = z.infer<typeof MembersAreaListItemSchema>
export type MembersAreaDetail = z.infer<typeof MembersAreaDetailSchema>
export type MembersUploadUrl = z.infer<typeof MembersUploadUrlSchema>
export type MembersVideo = z.infer<typeof MembersVideoSchema>
export type MembersVideoStatus = z.infer<typeof MembersVideoStatusSchema>
export type MembersVideoUpload = z.infer<typeof MembersVideoUploadSchema>
